import { useCallback, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from '@tanstack/react-router';
import { zodResolver } from '@/lib/zod-resolver';
import { toast } from 'sonner';
import { CheckCircle2, UserPlus, CalendarPlus } from 'lucide-react';
import type { DocumentResponse, EntityResponse, ProviderResponse } from '@vagusx/clients';
import { updateDocumentSchema, type UpdateDocumentRequest } from '@vagusx/clients';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { SaveButton } from '@/components/custom/save-button';
import { Button } from '@/components/ui/button';
import { formatDate } from '@/lib/format';
import { getEntityClient, getProviderClient } from '@/lib/api-clients';
import {
  useUpdateDocument,
  useMatchPatient,
  useUnmatchPatient,
  useCreatePatientFromDocument,
  useMatchReferringProvider,
  useMatchRenderingProvider,
  useUnmatchRenderingProvider,
  useMatchPayer,
} from '@/hooks/use-documents';
import { AsyncCombobox } from '@/components/autocomplete/async-combobox';
import { PatientCombobox } from '@/components/custom/patient-combobox';

import { isSectionVisible } from './constants';

// ⚠️ SYNC WITH PRINT VIEW
// The on-screen form AND `DocumentPrintView.tsx` render the
// same extraction data. Anytime you add/remove/rename a field or section
// here — especially a new `<Section>` block or a new `form.register(...)`
// line — update the print view too. They share `isSectionVisible()` from
// constants.ts so classification gating stays in sync automatically, but
// the list of fields per section is hand-maintained in both places.

interface DocumentDataPanelProps {
  document: DocumentResponse;
  onDirtyChange?: (isDirty: boolean) => void;
}

function buildFormValues(d: DocumentResponse): UpdateDocumentRequest {
  return {
    authNumber: d.authNumber ?? '',
    status: d.status ?? '',
    urgency: d.urgency ?? '',
    patientName: d.patientName ?? '',
    patientDob: d.patientDob ?? '',
    patientMemberId: d.patientMemberId ?? '',
    patientPhone: d.patientPhone ?? '',
    patientAddress: d.patientAddress ?? '',
    referringProviderName: d.referringProviderName ?? '',
    referringProviderNpi: d.referringProviderNpi ?? '',
    referringProviderPhone: d.referringProviderPhone ?? '',
    referringProviderFax: d.referringProviderFax ?? '',
    referringPracticeName: d.referringPracticeName ?? '',
    referringPracticeFax: d.referringPracticeFax ?? '',
    renderingProviderName: d.renderingProviderName ?? '',
    renderingProviderNpi: d.renderingProviderNpi ?? '',
    renderingPracticeName: d.renderingPracticeName ?? '',
    renderingPracticeFax: d.renderingPracticeFax ?? '',
    pcpName: d.pcpName ?? '',
    pcpNpi: d.pcpNpi ?? '',
    pcpPhone: d.pcpPhone ?? '',
    healthPlan: d.healthPlan ?? '',
    insuranceNetwork: d.insuranceNetwork ?? '',
    validFrom: d.validFrom ?? '',
    validTo: d.validTo ?? '',
    dateOfServiceFrom: d.dateOfServiceFrom ?? '',
    dateOfServiceTo: d.dateOfServiceTo ?? '',
    requestId: d.requestId ?? '',
    denialId: d.denialId ?? '',
    notes: d.notes ?? '',
    diagnosisCodes: d.diagnosisCodes ?? [],
    serviceCodes: d.serviceCodes ?? [],
  };
}

export function DocumentDataPanel({
  document,
  onDirtyChange,
}: DocumentDataPanelProps): React.ReactElement {
  const navigate = useNavigate();
  const updateDocument = useUpdateDocument();
  const matchPatient = useMatchPatient();
  const unmatchPatient = useUnmatchPatient();
  const createPatient = useCreatePatientFromDocument();
  const matchReferring = useMatchReferringProvider();
  const matchRendering = useMatchRenderingProvider();
  const unmatchRendering = useUnmatchRenderingProvider();
  const matchPayer = useMatchPayer();

  const form = useForm<UpdateDocumentRequest>({
    resolver: zodResolver(updateDocumentSchema),
    defaultValues: buildFormValues(document),
  });

  useEffect(() => {
    form.reset(buildFormValues(document));
  }, [document, form]);

  // Report dirty state to parent
  const isDirty = form.formState.isDirty;
  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  const onSubmit = async (data: UpdateDocumentRequest): Promise<void> => {
    // Date inputs ("" when cleared) are nullable in the DB — send null, not "".
    const dateFields = [
      'patientDob',
      'validFrom',
      'validTo',
      'dateOfServiceFrom',
      'dateOfServiceTo',
    ] as const;
    const payload = { ...data };
    for (const f of dateFields) {
      if (payload[f] === '') payload[f] = null;
    }
    await updateDocument.mutateAsync({ id: document.id, data: payload });
    toast.success('Document saved');
  };

  const searchReferringEntities = useCallback(async (_query: string, signal: AbortSignal) => {
    const result = await getEntityClient().search({
      searchTerm: _query,
      type: 'referring_provider',
      limit: 20,
    });
    if (signal.aborted) return [];
    return result.items;
  }, []);

  const providerCacheRef = useRef<ProviderResponse[] | null>(null);
  const searchProvidersAsync = useCallback(
    async (_query: string, _signal: AbortSignal): Promise<ProviderResponse[]> => {
      if (!providerCacheRef.current) {
        const result = await getProviderClient().list();
        providerCacheRef.current = result.items;
      }
      const term = _query.toLowerCase();
      return providerCacheRef.current
        .filter((p) => `${p.firstName} ${p.lastName}`.toLowerCase().includes(term))
        .slice(0, 10);
    },
    []
  );

  const searchPayerEntities = useCallback(async (_query: string, signal: AbortSignal) => {
    const result = await getEntityClient().search({
      searchTerm: _query,
      type: 'payer',
      limit: 20,
    });
    if (signal.aborted) return [];
    return result.items;
  }, []);

  const handlePatientSelect = useCallback(
    (id: string | null, label: string) => {
      if (id) {
        matchPatient.mutate({ id: document.id, patientId: id });
      } else if (document.patientId) {
        unmatchPatient.mutate(document.id);
      }
      form.setValue('patientName', label, { shouldDirty: true });
    },
    [matchPatient, unmatchPatient, document.id, document.patientId, form]
  );

  const handleReferringSelect = useCallback(
    (id: string | null, label: string) => {
      if (id) {
        matchReferring.mutate({ id: document.id, entityId: id });
        form.setValue('referringProviderName', label, { shouldDirty: true });
      }
    },
    [matchReferring, document.id, form]
  );

  const handlePayerSelect = useCallback(
    (id: string | null, label: string) => {
      if (id) {
        matchPayer.mutate({ id: document.id, entityId: id });
        form.setValue('healthPlan', label, { shouldDirty: true });
      }
    },
    [matchPayer, document.id, form]
  );

  return (
    <form
      id="doc-extraction-form"
      onSubmit={form.handleSubmit(onSubmit)}
      className="flex h-full flex-col"
    >
      {/* Scrollable form content */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden p-4 space-y-5">
        {/* Authorization */}
        {isSectionVisible('authorization', document.classification) && (
          <Section title="Authorization">
            <div className="grid grid-cols-3 gap-3">
              <FormField label="Auth #" {...form.register('authNumber')} />
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Status</Label>
                <Select
                  value={form.watch('status') ?? ''}
                  onValueChange={(v) => form.setValue('status', v, { shouldDirty: true })}
                >
                  <SelectTrigger className="h-8 w-full text-sm">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="denied">Denied</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                    <SelectItem value="modified">Modified</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Urgency</Label>
                <Select
                  value={form.watch('urgency') ?? ''}
                  onValueChange={(v) => form.setValue('urgency', v, { shouldDirty: true })}
                >
                  <SelectTrigger className="h-8 w-full text-sm">
                    <SelectValue placeholder="Select urgency" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="stat">Stat</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                    <SelectItem value="routine">Routine</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <FormField label="Valid From" {...form.register('validFrom')} type="date" />
              <FormField label="Valid To" {...form.register('validTo')} type="date" />
            </div>
          </Section>
        )}

        {/* Patient */}
        {isSectionVisible('patient', document.classification) && (
          <Section title="Patient" matched={!!document.patientId}>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Name</Label>
              <PatientCombobox
                value={document.patientId ?? null}
                onValueChange={handlePatientSelect}
                initialLabel={document.patientName ?? ''}
                allowFreeText
              />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <FormField label="DOB" {...form.register('patientDob')} type="date" />
              <FormField label="Member ID" {...form.register('patientMemberId')} />
              <FormField label="Phone" {...form.register('patientPhone')} />
            </div>
            <FormField label="Address" {...form.register('patientAddress')} />
            {/* When no patient is linked, offer creating one in the EHR from the
                extracted demographics (ADR-089). The new patient is created
                upstream and linked back to this document. */}
            {!document.patientId && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={createPatient.isPending}
                onClick={() =>
                  createPatient.mutate(document.id, {
                    onSuccess: () => toast.success('Patient created in EHR and linked'),
                  })
                }
              >
                <UserPlus className="size-4" />
                {createPatient.isPending ? 'Creating patient…' : 'Create patient in EHR'}
              </Button>
            )}
            {/* Once the patient is linked, schedule a visit straight from the
                referral — opens the appointments day view with this patient
                pre-selected, ready to drop onto an open slot. */}
            {document.patientId && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() =>
                  navigate({
                    to: '/appointments',
                    search: {
                      view: 'slots',
                      bookPatientId: document.patientId ?? undefined,
                      bookPatientName: document.patientName ?? undefined,
                    },
                  })
                }
              >
                <CalendarPlus className="size-4" />
                Schedule appointment
              </Button>
            )}
          </Section>
        )}

        {/* Referring Provider */}
        {isSectionVisible('referringProvider', document.classification) && (
          <Section title="Referring Provider" matched={!!document.referringProviderEntityId}>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Name</Label>
              <AsyncCombobox<EntityResponse>
                value={document.referringProviderEntityId ?? null}
                onValueChange={handleReferringSelect}
                searchFn={searchReferringEntities}
                getItemId={(e) => e.id}
                getItemLabel={(e) => e.name}
                renderItem={(e) => (
                  <div>
                    <div className="font-medium">{e.name}</div>
                    {e.npi && <div className="text-xs text-muted-foreground">NPI: {e.npi}</div>}
                  </div>
                )}
                placeholder="Search referring providers..."
                initialLabel={document.referringProviderName ?? ''}
                allowFreeText
              />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <FormField label="NPI" {...form.register('referringProviderNpi')} />
              <FormField label="Phone" {...form.register('referringProviderPhone')} />
              <FormField label="Fax" {...form.register('referringProviderFax')} />
            </div>
          </Section>
        )}

        {/* Referring Practice */}
        {isSectionVisible('referringPractice', document.classification) && (
          <Section title="Referring Practice" matched={!!document.referringPracticeEntityId}>
            <FormField label="Name" {...form.register('referringPracticeName')} />
            <FormField label="Fax" {...form.register('referringPracticeFax')} />
          </Section>
        )}

        {/* PCP */}
        {isSectionVisible('pcp', document.classification) && (
          <Section title="Primary Care Physician">
            <FormField label="Name" {...form.register('pcpName')} />
            <div className="grid grid-cols-2 gap-3">
              <FormField label="NPI" {...form.register('pcpNpi')} />
              <FormField label="Phone" {...form.register('pcpPhone')} />
            </div>
          </Section>
        )}

        {/* Rendering Provider */}
        {isSectionVisible('renderingProvider', document.classification) && (
          <Section title="Rendering Provider" matched={!!document.renderingProviderId}>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Name</Label>
              <AsyncCombobox<ProviderResponse>
                value={document.renderingProviderId ?? null}
                onValueChange={(providerId, label) => {
                  if (providerId) {
                    matchRendering.mutate({ id: document.id, providerId });
                  } else if (document.renderingProviderId) {
                    unmatchRendering.mutate(document.id);
                  }
                }}
                searchFn={searchProvidersAsync}
                getItemId={(p) => p.id}
                getItemLabel={(p) => `${p.firstName} ${p.lastName}`}
                renderItem={(p) => (
                  <div>
                    <div className="font-medium">
                      {p.firstName} {p.lastName}
                    </div>
                    {p.specialty && (
                      <div className="text-xs text-muted-foreground">{p.specialty}</div>
                    )}
                  </div>
                )}
                placeholder="Search providers..."
                initialLabel={document.renderingProviderName ?? ''}
                allowFreeText
              />
            </div>
            <FormField label="NPI" {...form.register('renderingProviderNpi')} />
          </Section>
        )}

        {/* Rendering Practice */}
        {isSectionVisible('renderingPractice', document.classification) && (
          <Section title="Rendering Practice">
            <FormField label="Name" {...form.register('renderingPracticeName')} />
            <FormField label="Fax" {...form.register('renderingPracticeFax')} />
          </Section>
        )}

        {/* Insurance / Payer */}
        {isSectionVisible('insurance', document.classification) && (
          <Section title="Insurance / Payer" matched={!!document.payerEntityId}>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Health Plan</Label>
              <AsyncCombobox<EntityResponse>
                value={document.payerEntityId ?? null}
                onValueChange={handlePayerSelect}
                searchFn={searchPayerEntities}
                getItemId={(e) => e.id}
                getItemLabel={(e) => e.name}
                renderItem={(e) => <div className="font-medium">{e.name}</div>}
                placeholder="Search payers..."
                initialLabel={document.healthPlan ?? ''}
                allowFreeText
              />
            </div>
            <FormField label="Network" {...form.register('insuranceNetwork')} />
          </Section>
        )}

        {/* Dates of Service */}
        {isSectionVisible('datesOfService', document.classification) && (
          <Section title="Dates of Service">
            <div className="grid grid-cols-2 gap-3">
              <FormField label="From" {...form.register('dateOfServiceFrom')} type="date" />
              <FormField label="To" {...form.register('dateOfServiceTo')} type="date" />
            </div>
          </Section>
        )}

        {/* Request ID / Number */}
        {isSectionVisible('requestId', document.classification) && (
          <Section title="Request ID / Number">
            <FormField label="Request ID" {...form.register('requestId')} />
          </Section>
        )}

        {/* Denial ID / Number */}
        {isSectionVisible('denialId', document.classification) && (
          <Section title="Denial ID / Number">
            <FormField label="Denial ID" {...form.register('denialId')} />
          </Section>
        )}

        {/* Diagnosis Codes */}
        {isSectionVisible('diagnosisCodes', document.classification) && (
          <Section title="Diagnosis Codes">
            {(document.diagnosisCodes ?? []).length > 0 ? (
              <div className="space-y-1">
                {document.diagnosisCodes!.map((dx, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm">
                    <Badge variant="outline" className="font-mono text-xs shrink-0">
                      {dx.code}
                    </Badge>
                    {dx.description && (
                      <span className="text-muted-foreground truncate">{dx.description}</span>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <span className="text-sm text-muted-foreground">No diagnosis codes</span>
            )}
          </Section>
        )}

        {/* Service Codes */}
        {isSectionVisible('serviceCodes', document.classification) && (
          <Section title="Service Codes">
            {(document.serviceCodes ?? []).length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="h-8 text-xs">Code</TableHead>
                    <TableHead className="h-8 text-xs">Description</TableHead>
                    <TableHead className="h-8 text-xs">Qty</TableHead>
                    <TableHead className="h-8 text-xs">POS</TableHead>
                    <TableHead className="h-8 text-xs">Effective</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {document.serviceCodes!.map((svc, i) => (
                    <TableRow key={i}>
                      <TableCell className="py-1.5 align-top whitespace-normal break-words">
                        <Badge variant="outline" className="font-mono text-xs">
                          {svc.code}
                        </Badge>
                      </TableCell>
                      <TableCell className="py-1.5 align-top text-muted-foreground whitespace-normal break-words">
                        {svc.description ?? '—'}
                      </TableCell>
                      <TableCell className="py-1.5 align-top text-muted-foreground">
                        {svc.units ?? '—'}
                      </TableCell>
                      <TableCell className="py-1.5 align-top text-muted-foreground whitespace-normal break-words">
                        {svc.placeOfService ?? '—'}
                      </TableCell>
                      <TableCell className="py-1.5 align-top text-muted-foreground whitespace-normal break-words text-xs">
                        {svc.effectiveFrom || svc.effectiveTo
                          ? [formatDate(svc.effectiveFrom), formatDate(svc.effectiveTo)]
                              .filter(Boolean)
                              .join(' – ') || '—'
                          : '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <span className="text-sm text-muted-foreground">No service codes</span>
            )}
          </Section>
        )}

        {/* Notes */}
        {isSectionVisible('notes', document.classification) && (
          <Section title="Notes">
            <div className="space-y-1.5">
              <Textarea {...form.register('notes')} rows={3} className="text-sm resize-none" />
            </div>
          </Section>
        )}
      </div>

      {/* Fixed footer */}
      <div className="flex justify-end shrink-0 border-t bg-background px-4 py-3">
        <SaveButton type="submit" size="sm" isPending={updateDocument.isPending} />
      </div>
    </form>
  );
}

function Section({
  title,
  matched,
  children,
}: {
  title: string;
  /** true → ✓ (linked to a record); otherwise no indicator. */
  matched?: boolean;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <div className="space-y-3">
      <h3 className="flex items-center gap-1.5 text-sm font-medium text-foreground">
        {title}
        {matched && (
          <CheckCircle2
            className="h-4 w-4 text-green-600"
            aria-label="Matched to existing record"
          />
        )}
      </h3>
      <div className="space-y-3 pl-0">{children}</div>
    </div>
  );
}

function FormField({
  label,
  type = 'text',
  ...inputProps
}: {
  label: string;
  type?: string;
} & React.InputHTMLAttributes<HTMLInputElement>): React.ReactElement {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <Input type={type} className="h-8 text-sm" {...inputProps} />
    </div>
  );
}
